stimulusApp.register(
  "property-questions",
  class extends Stimulus.Controller {
    static targets = [
      "input",
      "submit",
      "error",
      "panel",
      "messages",
      "launcher",
      "launcherLabel",
      "reply",
      "question",
      "questionTemplate",
      "pending",
      "retry",
    ]
    static values = { mobile: Boolean }

    static get shouldLoad() {
      return CSS.supports("selector(:popover-open)")
    }

    #unregisterBack
    #collapseOffset = 500

    connect() {
      this.updateLauncher()
    }

    disconnect() {
      this.#releaseMobile()
    }

    updateLauncher() {
      if (this.panelTarget.matches(":popover-open")) return

      const scrolled = "property-questions_scrolled"
      const collapsed = this.element.classList.contains(scrolled)
      this.element.classList.toggle(
        scrolled,
        window.scrollY > (collapsed ? 0 : this.#collapseOffset),
      )
    }

    launcherLabelTargetConnected(label) {
      label.style.setProperty(
        "--property-questions--launcher--label-width",
        `${label.scrollWidth}px`,
      )
    }

    replyTargetConnected(reply) {
      this.#animateMessage(reply, "property-questions--result_arriving")
    }

    questionTargetConnected(question) {
      this.#animateMessage(question, "property-questions--question_arriving")
    }

    retryTargetConnected(retry) {
      this.#restoreQuestion(retry.dataset.question)
      retry.removeAttribute("data-property-questions-target")
    }

    pendingTargetDisconnected(exchange) {
      exchange.querySelector(".property-questions--progress").remove()
    }

    async #animateMessage(message, arrivingClass) {
      if (this.panelTarget.matches(":popover-open")) {
        await Promise.allSettled(
          message.getAnimations().map((animation) => animation.finished),
        )
      }
      message.classList.remove(arrivingClass)
    }

    skipMotion() {
      this.element.classList.add("property-questions_instant")
    }

    allowMotion() {
      this.element.classList.remove("property-questions_instant")
    }

    prepareToggle(event) {
      const open = event.newState === "open"
      this.launcherTarget.inert = open
      if (open) Activity.capture("property questions opened")
      if (!this.mobileValue) return

      if (open) {
        if (!this.#unregisterBack) {
          HB.controlOffset.saveOffset()
          HB.fullscreenOverlay.beforeMount()
          this.#unregisterBack = HB.nativeBack.push(() =>
            this.panelTarget.hidePopover(),
          )
        }
        this.#updateViewport()
        window.visualViewport.addEventListener("resize", this.#updateViewport)
        window.visualViewport.addEventListener("scroll", this.#updateViewport)
      } else {
        this.#releaseMobile()
      }
    }

    #updateViewport = () => {
      const viewport = window.visualViewport
      const bottom = window.innerHeight - viewport.height - viewport.offsetTop
      this.panelTarget.style.setProperty(
        "--property-questions--viewport-bottom",
        `${Math.max(0, bottom)}px`,
      )
      this.panelTarget.style.setProperty(
        "--property-questions--viewport-height",
        `${viewport.height}px`,
      )
    }

    #releaseMobile() {
      if (!this.mobileValue) return

      window.visualViewport.removeEventListener("resize", this.#updateViewport)
      window.visualViewport.removeEventListener("scroll", this.#updateViewport)
      if (this.#unregisterBack) {
        this.#unregisterBack()
        this.#unregisterBack = null
        HB.fullscreenOverlay.beforeUnmount()
        HB.controlOffset.restoreOffset()
      }
    }

    preventOverlap(event) {
      if (event.target.hasAttribute("aria-busy")) event.preventDefault()
    }

    start(event) {
      this.errorTarget.classList.add("hidden")
      if (event.detail.formSubmission.submitter !== this.submitTarget) return

      const question = event.detail.formSubmission.body.get("question").trim()
      const content = this.questionTemplateTarget.content.cloneNode(true)
      content.firstElementChild.firstElementChild.textContent = question
      this.messagesTarget.append(content.firstElementChild)
      this.inputTarget.value = ""
      this.refresh()
    }

    finish(event) {
      if (
        event.detail.error ||
        !event.detail.fetchResponse ||
        event.detail.fetchResponse.statusCode >= 500
      ) {
        this.#restoreQuestion()
        this.errorTarget.classList.remove("hidden")
      }
    }

    refresh() {
      if (this.panelTarget.matches(":popover-open") && this.hasInputTarget) {
        this.resizeInput()
        this.scrollMessages()
      }
    }

    close() {
      this.panelTarget.hidePopover()
    }

    scrollMessages() {
      const messages = this.messagesTarget
      const exchange = messages.lastElementChild
      const reply = exchange?.querySelector(".property-questions--result")
      const padding = parseFloat(getComputedStyle(messages).paddingTop)
      const availableHeight = messages.clientHeight - padding * 2

      if (reply && exchange.offsetHeight > availableHeight) {
        messages.scrollTop = reply.offsetTop - messages.offsetTop - padding
      } else {
        messages.scrollTop = messages.scrollHeight
      }
    }

    keepInputFocus(event) {
      if (document.activeElement === this.inputTarget) event.preventDefault()
    }

    fail(event) {
      event.preventDefault()
      this.#restoreQuestion()
      this.errorTarget.classList.remove("hidden")
    }

    #restoreQuestion(question) {
      if (!question && this.hasPendingTarget) {
        question = this.pendingTarget.firstElementChild.textContent
      }
      if (!question) return
      if (this.inputTarget.value) {
        if (this.hasPendingTarget) {
          this.pendingTarget.removeAttribute("data-property-questions-target")
        }
        return
      }

      this.inputTarget.value = question
      if (this.hasPendingTarget) this.pendingTarget.remove()
      if (this.hasRetryTarget) this.retryTarget.firstElementChild.remove()
      this.resizeInput()
      this.scrollMessages()
    }

    resizeInput() {
      const input = this.inputTarget
      input.style.height = "auto"
      input.style.height = `${input.scrollHeight + input.offsetHeight - input.clientHeight}px`
    }

    send(event) {
      if (event.key !== "Enter" || event.shiftKey || event.isComposing) return

      event.preventDefault()
      if (this.inputTarget.value.trim()) {
        this.inputTarget.form.requestSubmit(this.submitTarget)
      }
    }

    async followSource(event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return

      const section = document.getElementById(event.currentTarget.hash.slice(1))
      if (!section) return

      const headings = ".place-info--title, .place-info--subtitle"
      const title = section?.matches(headings)
        ? section
        : section?.querySelector(headings)
      event.preventDefault()
      if (this.mobileValue) this.panelTarget.hidePopover()
      document
        .querySelectorAll(
          ".place-info--title_highlighted, .place-info--subtitle_highlighted",
        )
        .forEach((heading) => {
          heading.classList.remove(
            "place-info--title_highlighted",
            "place-info--subtitle_highlighted",
          )
        })
      if (!title) {
        Viewport.scrollTo($(section))
        return
      }

      const container = title.closest(".toggle--container")
      if (
        this.mobileValue &&
        container?.matches("[details=on]:not(.toggle--container_opened)")
      ) {
        container.querySelector(".toggle--title").click()
      }
      const halalFeatures = title.closest(".place-halal-features")
      if (
        this.mobileValue &&
        halalFeatures?.matches(".place-halal-features_expandable") &&
        !halalFeatures.matches(".place-halal-features_expanded")
      ) {
        halalFeatures.click()
      }

      const highlight = title.matches(".place-info--subtitle")
        ? "place-info--subtitle_highlighted"
        : "place-info--title_highlighted"
      title.classList.add(highlight)
      const content = title.closest(".toggle, .place-halal-features--body")
      const animations = content?.getAnimations({ subtree: true }) || []
      if (animations.length) {
        await Promise.allSettled(
          animations.map((animation) => animation.finished),
        )
        if (!title.classList.contains(highlight)) return
      }
      Viewport.scrollTo($(title))
    }
  },
)
